import { create } from "zustand";
import { devtools, subscribeWithSelector } from "zustand/middleware";

import type { Room } from "../view-model";

interface RoomsStore {
  // State
  rooms: Room[];
  activeRoom: Room | undefined;
  isLoading: boolean;
  error: string | null;
  lastUpdated: number;

  // Actions
  setRooms: (rooms: Room[]) => void;
  setActiveRoom: (room: Room | undefined) => void;
  updateRoom: (roomId: string, updates: Partial<Room>) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearErrors: () => void;

  // Selectors
  getRooms: () => Room[];
  getActiveRoom: () => Room | undefined;
  getRoomById: (roomId: string) => Room | undefined;
  getRoomsByDevice: (deviceId: string) => Room[];
}

// Restore activeRoom from localStorage synchronously to avoid loading flicker on refresh
const getStoredActiveRoom = (): Room | undefined => {
  if (typeof window === "undefined") return undefined;
  try {
    const stored = window.localStorage.getItem("activeRoom");
    return stored ? JSON.parse(stored) : undefined;
  } catch {
    return undefined;
  }
};

export const useRoomsStore = create<RoomsStore>()(
  devtools(
    subscribeWithSelector((set, get) => ({
      // Initial state
      rooms: [],
      activeRoom: getStoredActiveRoom(),
      isLoading: false,
      error: null,
      lastUpdated: 0,

      // Actions
      setRooms: (rooms: Room[]) => {
        set({
          rooms,
          lastUpdated: Date.now(),
          isLoading: false,
          error: null,
        });
      },

      setActiveRoom: (room: Room | undefined) => {
        set({ activeRoom: room });

        // Persist to localStorage
        if (typeof window !== "undefined") {
          window.localStorage.setItem("activeRoom", JSON.stringify(room));
        }
      },

      updateRoom: (roomId: string, updates: Partial<Room>) => {
        set((state) => ({
          rooms: state.rooms.map((room) =>
            room.id === roomId ? { ...room, ...updates } : room
          ),
          lastUpdated: Date.now(),
        }));
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      setError: (error: string | null) => {
        set({ error, isLoading: false });
      },

      clearErrors: () => {
        set({ error: null });
      },

      // Selectors
      getRooms: () => get().rooms,

      getActiveRoom: () => get().activeRoom,

      getRoomById: (roomId: string) =>
        get().rooms.find((room) => room.id === roomId),

      getRoomsByDevice: (deviceId: string) =>
        get().rooms.filter((room) => room.deviceIds?.includes(deviceId)),
    })),
    {
      name: "rooms-store", // Name that will appear in Redux DevTools
    }
  )
);
